import { gql } from '@apollo/client';

export const PUBLISH_FIELDS = gql`
  fragment PublishFields on SocialPublish {
    id
    platform
    mode
    status
    caption
    fail_reason
    clip_id
    social_connection_id
    title
    description
    privacy_status
    made_for_kids
    provider_post_id
    facebook_post_kind
    scheduled_at
    cancelled_at
    createdAt
    media_kind
    image_count
    publish_queue {
      id
      name
    }
  }
`;

export const MY_CLIP_PUBLISHES = gql`
  ${PUBLISH_FIELDS}
  query MyClipPublishes($clipIds: [ID!]!) {
    myClipPublishes(clipIds: $clipIds) {
      ...PublishFields
    }
  }
`;

/** TikTok's pending-drafts cap is enforced per CONNECTION across every clip
 *  in a rolling 24h (see the backend's sendClipToTikTokDrafts.js /
 *  publishQuota.js), not per clip. `SendToTikTokDialog` uses this — instead
 *  of `MY_CLIP_PUBLISHES` scoped to the one clip being sent — so the quota it
 *  shows matches what the backend will actually enforce. */
export const MY_CONNECTION_PUBLISHES = gql`
  ${PUBLISH_FIELDS}
  query MyConnectionPublishes($socialConnectionId: ID!) {
    myConnectionPublishes(socialConnectionId: $socialConnectionId) {
      ...PublishFields
    }
  }
`;

// What the TikTok dialogs preview, and whether the render carries the
// AutoClipper watermark (TikTok refuses it). The status fields let the backend
// skip its queue-position ETA lookups for a finished clip.
export const TIKTOK_CLIP_PREVIEW = gql`
  query TikTokClipPreview($id: ID!) {
    myClip(id: $id) {
      id
      clipping_status
      preparing_status
      rendering_status
      finished_video_url
      finished_video_thumbnail_url
      watermark
    }
  }
`;

export const TIKTOK_CREATOR_INFO = gql`
  query TikTokCreatorInfo($socialConnectionId: ID!) {
    tiktokCreatorInfo(socialConnectionId: $socialConnectionId) {
      creator_avatar_url
      creator_username
      creator_nickname
      privacy_level_options
      comment_disabled
      duet_disabled
      stitch_disabled
      max_video_post_duration_sec
    }
  }
`;

export const PUBLISH_CLIP_TO_TIKTOK = gql`
  ${PUBLISH_FIELDS}
  mutation PublishClipToTikTok(
    $clipId: ID!
    $socialConnectionId: ID!
    $title: String
    $privacyLevel: String!
    $disableComment: Boolean
    $disableDuet: Boolean
    $disableStitch: Boolean
    $brandOrganicToggle: Boolean
    $brandContentToggle: Boolean
    $isAigc: Boolean
    $imageUrls: [String!]
  ) {
    publishClipToTikTok(
      clipId: $clipId
      socialConnectionId: $socialConnectionId
      title: $title
      privacyLevel: $privacyLevel
      disableComment: $disableComment
      disableDuet: $disableDuet
      disableStitch: $disableStitch
      brandOrganicToggle: $brandOrganicToggle
      brandContentToggle: $brandContentToggle
      isAigc: $isAigc
      imageUrls: $imageUrls
    ) {
      ...PublishFields
    }
  }
`;

export const SEND_CLIP_TO_TIKTOK_DRAFTS = gql`
  ${PUBLISH_FIELDS}
  mutation SendClipToTikTokDrafts($clipId: ID!, $socialConnectionId: ID!, $caption: String) {
    sendClipToTikTokDrafts(
      clipId: $clipId
      socialConnectionId: $socialConnectionId
      caption: $caption
    ) {
      ...PublishFields
    }
  }
`;

export const PUBLISH_CLIP_TO_INSTAGRAM = gql`
  ${PUBLISH_FIELDS}
  mutation PublishClipToInstagram(
    $clipId: ID!
    $socialConnectionId: ID!
    $caption: String
    $imageUrls: [String!]
  ) {
    publishClipToInstagram(
      clipId: $clipId
      socialConnectionId: $socialConnectionId
      caption: $caption
      imageUrls: $imageUrls
    ) {
      ...PublishFields
    }
  }
`;

export const PUBLISH_CLIP_TO_FACEBOOK = gql`
  ${PUBLISH_FIELDS}
  mutation PublishClipToFacebook(
    $clipId: ID!
    $socialConnectionId: ID!
    $caption: String
    $imageUrls: [String!]
  ) {
    publishClipToFacebook(
      clipId: $clipId
      socialConnectionId: $socialConnectionId
      caption: $caption
      imageUrls: $imageUrls
    ) {
      ...PublishFields
    }
  }
`;

export const PUBLISH_CLIP_TO_YOUTUBE = gql`
  ${PUBLISH_FIELDS}
  mutation PublishClipToYouTube(
    $clipId: ID!
    $socialConnectionId: ID!
    $title: String!
    $description: String
    $privacyStatus: String!
    $madeForKids: Boolean!
  ) {
    publishClipToYouTube(
      clipId: $clipId
      socialConnectionId: $socialConnectionId
      title: $title
      description: $description
      privacyStatus: $privacyStatus
      madeForKids: $madeForKids
    ) {
      ...PublishFields
    }
  }
`;
